import { ApiClient } from '@/services/api/client'
import { EventEmitter } from 'eventemitter3'
import _ from 'lodash'

import * as ImageConfig from '@/config/assets/image'
import { log } from '@/log'
import { ApiNoveltyCampaign } from '@/models/api/campaign/noveltyCampaign'
import ApiStock from '@/models/api/product/stock'
import { ApiProductclass } from '@/models/api/productclass'
import BehaviorConfig from '@/models/app-config/behavior/behavior'
import * as StockFactory from '@/models/item/sku/factory/stock'
import { Novelty, NoveltyCampaign, NoveltyFku } from '@/models/novelty/novelty'
import ChunkedRequestExecuter from '@/services/api/async/chunked-request-executer'
import GetStockStatus from '@/usecase/get-stock-status'
import { Either, Left, Right } from 'fp-ts/lib/Either'
import CampaignService from './api/campaign'

import * as SKUFactory from '@/models/item/sku/factory/sku'

const namespace = 'novelty'

type NoveltyServiceEvent = 'novelty-not-found' | 'finish'

/**
 * TODO: APIクライアントとしては責務が過剰。APIクライアントとして見なくても過剰。
 *
 * - 単にAPIを叩いて、ノベルティリソースを無加工で返すモジュール
 * - ノベルティリソースを元に、ドメインオブジェクトとしての「ノベルティ」を組み立てるモジュール
 * - verifiedな「ノベルティ」に絞り込む、1件だけ参照するといった、ノベルティ取得のルールを表現する仕様オブジェクト
 * - ノベルティ取得のルールを適用して絞り込むモジュール
 * ぐらいに分割したい。
 */
export class NoveltyService {
  private readonly eventEmitter = new EventEmitter()

  constructor(
    private readonly appId: string = 'MyColorWeb',
    private readonly behavior: BehaviorConfig
    ) {}

  onNoveltyNotFound(listener: () => void): NoveltyService {
    return this.on('novelty-not-found', listener)
  }
  onFinish(listener: () => void): NoveltyService {
    return this.on('finish', listener)
  }

  get campaignService(): CampaignService {
    return new CampaignService(this.appId)
  }

  /**
   * In principle, only a single record of novelty campaign will be returned.
   * See https://www.chatwork.com/#!rid104448136-1187288573489971200
   */
  async getFirst(): Promise<Either<Error, NoveltyCampaign | null>> {
    const campaignsResult = await this.listVerifiedCampaign()

    if (campaignsResult.isLeft()) {
      this.emit('finish')

      return new Left(campaignsResult.value)
    }

    if (campaignsResult.value.length > 1) {
      log.warn('There are multiple active novelty campaign, though supposed to be one.')
    }

    const campaign = _.first(campaignsResult.value)

    if (!campaign) {
      this.emit('novelty-not-found')
      this.emit('finish')

      return new Right(null)
    }

    try {
      const detail = await this.joinNoveltyItems(campaign)

      return new Right(detail)
    } catch (err) {
      return new Left(err)
    } finally {
      this.emit('finish')
    }
  }

  private async listCampaign(): Promise<Either<Error, ApiNoveltyCampaign[]>> {
    const response = await this.campaignService.listNovelty()
    log.debug({ service: `${namespace}/list`, response })

    if (response.isLeft()) {
      return new Left(response.value)
    }

    return new Right(
      _.map(response.value.body.data, (raw: any) => new ApiNoveltyCampaign(raw))
    )
  }

  private async listVerifiedCampaign(): Promise<Either<Error, ApiNoveltyCampaign[]>> {
    const campaignsResult = await this.listCampaign()

    return campaignsResult.map((campaigns) => {
      return campaigns.filter(campaign => campaign.isVerified)
    })
  }

  private async joinNoveltyItems(campaign: ApiNoveltyCampaign): Promise<NoveltyCampaign> {
    const fkuIds = campaign.noveltyList.map(novelty => novelty.id)
    const fkuList = await this.fetchFkuList(fkuIds)

    const noveltyFkuList: NoveltyFku[] = _.map(fkuList, (fku) => {
      return {
        fku,
        skuList: fku.parts,
      }
    })

    const noveltyList = this.convertToNoveltyDomainList(noveltyFkuList)

    /**
     * Make campaign info
     */
    return {
      campaign,
      noveltyList,
    }
  }

  private async fetchFkuList(fkuIds: string[]): Promise<ApiProductclass[]> {
    const chunkedRequest = ChunkedRequestExecuter.withLongId()
    const result =  await chunkedRequest.execute(
      fkuIds,
      async (ids: string[]) => await this.listNoveltyFkuList(ids)
    )
    const [
      successes,
      errors,
    ] = _.partition(result, r => r.isRight())

    if (errors.length > 0) {
      log.error({ errors })
      throw new FailedFetchFkuListError()
    }

    const itemList = _.flatten(successes.map(s => s.value))

    return itemList.map(item => new ApiProductclass(item))
  }

  private convertToNoveltyDomainList(fkuList: NoveltyFku[]): Novelty[] {
    return _.flatten(fkuList.map((fkuDetail) => {
      return fkuDetail.skuList.map((sku) => {
        return {
          skuId: sku.id,
          fkuId: fkuDetail.fku.id,
          productName: fkuDetail.fku.productName,
          color: fkuDetail.fku.color ? fkuDetail.fku.color.label : '',
          size: sku.getSize() || '',
          thumbnail: fkuDetail.fku.representativeImage ?
            fkuDetail.fku.representativeImage.url : ImageConfig.dummyImagePath,
          stock: this.getStockAmount(sku.stockDetail),
          maxSellCount: sku.maxSellCount,
          // 直接SKUドメインの持つ販売ステータスを参照している。
          // ApiProductを直接参照しているのに比べればマシだが、
          // ノベルティ商品とSKUのドメインを（サーバ側で）整理した上でノベルティ側のVOとして受け取るようにしたい。
          saleStatus: SKUFactory.create(sku).saleStatus,
        }
      })
    }))
  }

  private getStockAmount(apiStock: ApiStock) {
    return this.getStockStatus.amount(
      StockFactory.create(apiStock)
    )
  }

  private get getStockStatus(): GetStockStatus {
    return GetStockStatus.createViaConfig(this.behavior)
  }

  private on(eventName: 'novelty-not-found' | 'finish', listener: () => void): NoveltyService
  private on(eventName: NoveltyServiceEvent, listener: (...args: any[]) => void): NoveltyService {
    this.eventEmitter.on(eventName, listener)

    return this
  }
  private emit(eventName: 'novelty-not-found' | 'finish'): void
  private emit(eventName: NoveltyServiceEvent, ...args: any[]): void {
    this.eventEmitter.emit(eventName, ...args)
  }

  private async listNoveltyFkuList(ids: string[]): Promise<Either<Error, any[]>> {
    try {
      const response = await ApiClient
        /*
         * productclassないしFKU情報の取得は ProductclassService に集約しているが、
         * この箇所は一時的にやむを得ず、直接 productclass2 を使って良いものとする.
         *
         * 理由は下記.
         * - /item/fku はFKUとしてサービスが公開する情報を提供するAPIである.
         *   - そのため、ノベルティやサブスクリプション申し込み用商品といったリソースは公開の対象外である
         *   - しかしノベルティ商品構築には、まさにノベルティ商品の情報も必要である
         *   - そのため、特に制限が設けられることのない proxy を使って、直接取得させている
         *     - ただしこれは「そうせざるを得ない」という消極的な判断である
         * - 本来、これら「ノベルティ商品を構築するためのデータ集め」はclientで行うべきことではない
         *   - 知識の集約的にも、実装の一元化的にも、サーバー内部で実装されてしかるべき
         *   - したがって、ここでの「productclassを直接収集」は、サーバー側実装がclientに流出していると評価
         *   - であるならば、ここの実装は最終的にはサーバー側へ移植されるべきもの
         *   - サーバー側に移植された後なら、任意の形式でEngine APIを利用できる
         * - ノベルティ商品と通常商品は productclass という同じリソースを使っているが、扱いが大きく異なる
         *   - 通常商品は単発注文可能だが、ノベルティは通常商品の価格合計によって購入できるかどうか決まる
         *   - 通常商品は商品一覧や商品詳細で閲覧可能だが、ノベルティは不可能である
         *
         * 上記の通り、最終的にはここにある「カート内商品を構築するためのデータ集め」は全てサーバーに移植され、
         * clientからは一掃されるということが前提になっており、それまでの一時しのぎとして
         * proxy経由でproductclass2を使うものである.
         */
        .get(`/api/common-proxy/${this.appId}/productclass2`, {
          ids: ids.join(','),
          with: 'part,stock~detail,indexsummry',
        })

      log.debug({ service: `${namespace}/listNoveltyFkuList`, response })

      return new Right(response.body.data)
    } catch (error) {
      log.error({ service: `${namespace}/listNoveltyFkuList`, error })

      return new Left(error)
    }
  }
}

class FailedFetchFkuListError extends Error {}
